import { useState, useEffect } from "react";
import AdminLayout from "@/components/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Building2, Users, FileText, HelpCircle, LogIn, UserCheck, UserX, Clock, Ship, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";

import vesselRender1 from "@/assets/vessel-render-1.jpg";
import vesselRender2 from "@/assets/vessel-render-2.jpg";
import vesselRender3 from "@/assets/vessel-render-3.jpg";
import vesselRender4 from "@/assets/vessel-render-4.jpg";
import vesselRender5 from "@/assets/vessel-render-5.jpg";
import vesselRender6 from "@/assets/vessel-render-6.jpg";
import vesselRender7 from "@/assets/vessel-render-7.jpg";
import vesselRender8 from "@/assets/vessel-render-8.jpg";

const slideshowImages = [
  vesselRender1, vesselRender2, vesselRender3, vesselRender4,
  vesselRender5, vesselRender6, vesselRender7, vesselRender8,
];

type AuditLogin = { user_id: string; email: string; created_at: string };

const EDGE_BASE = `https://zxdwkagqcqojxcwlsdyc.supabase.co/functions/v1`;

const getAuthHeader = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return { "Authorization": `Bearer ${session?.access_token}`, "Content-Type": "application/json" };
};

type ClientLoginEntry = {
  user_id: string;
  email: string;
  full_name: string;
  company: string;
  logged_in_at: string;
  source: string;
};

type ClientSummary = {
  user_id: string;
  full_name: string;
  company: string;
  total_logins: number;
  last_login_audit: string | null;
  last_sign_in_at: string | null;
  logins: ClientLoginEntry[];
};

const fetchRecentLogins = async (): Promise<AuditLogin[]> => {
  const { data } = await supabase
    .from("audit_logs")
    .select("user_id, details, created_at")
    .eq("action", "login")
    .eq("resource_type", "auth")
    .order("created_at", { ascending: false })
    .limit(100);
  return (data ?? []).map((row: any) => ({
    user_id: row.user_id,
    email: (row.details as any)?.email ?? "",
    created_at: row.created_at,
  }));
};

const fetchClientLogins = async (): Promise<ClientSummary[]> => {
  const headers = await getAuthHeader();
  const res = await fetch(`${EDGE_BASE}/manage-users?action=client-logins`, { headers });
  if (!res.ok) throw new Error("Failed to fetch client logins");
  const data = await res.json();
  return data.clients ?? [];
};

const timeAgo = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const formatDateTime = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    " at " +
    d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
};

const AdminDashboard = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [expandedClient, setExpandedClient] = useState<string | null>(null);
  const [clientTab, setClientTab] = useState<"summary" | "history">("summary");
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideshowImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const { data: companyCount = 0 } = useQuery({
    queryKey: ["admin-company-count"],
    queryFn: async () => {
      const { count } = await supabase.from("companies").select("*", { count: "exact", head: true });
      return count ?? 0;
    },
  });

  const { data: userCount = 0 } = useQuery({
    queryKey: ["admin-user-count"],
    queryFn: async () => {
      const { count } = await supabase.from("profiles").select("*", { count: "exact", head: true });
      return count ?? 0;
    },
  });

  const { data: docCount = 0 } = useQuery({
    queryKey: ["admin-doc-count"],
    queryFn: async () => {
      const { count } = await supabase.from("documents").select("*", { count: "exact", head: true });
      return count ?? 0;
    },
  });

  const { data: pendingQuestions = 0 } = useQuery({
    queryKey: ["admin-pending-questions"],
    queryFn: async () => {
      const { count } = await supabase.from("technical_questions").select("*", { count: "exact", head: true }).eq("status", "pending");
      return count ?? 0;
    },
  });

  const { data: profiles = [] } = useQuery({
    queryKey: ["admin-profiles-dashboard"],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("user_id, full_name, companies(name)");
      return data ?? [];
    },
  });

  const { data: auditLogins = [], isLoading: loginsLoading } = useQuery<AuditLogin[]>({
    queryKey: ["admin-audit-logins"],
    queryFn: fetchRecentLogins,
    refetchInterval: 30000,
  });

  const { data: clientLogins = [], isLoading: clientLoginsLoading } = useQuery<ClientSummary[]>({
    queryKey: ["admin-client-logins"],
    queryFn: fetchClientLogins,
    refetchInterval: 60000,
  });

  const backfillMutation = useMutation({
    mutationFn: async () => {
      const headers = await getAuthHeader();
      const res = await fetch(`${EDGE_BASE}/manage-users?action=backfill-logins`, { method: "POST", headers });
      if (!res.ok) throw new Error("Backfill failed");
      return res.json();
    },
    onSuccess: (data) => {
      toast.success(`Backfilled ${data.backfilled} login records from ${data.total_clients} client accounts`);
      queryClient.invalidateQueries({ queryKey: ["admin-client-logins"] });
      queryClient.invalidateQueries({ queryKey: ["admin-audit-logins"] });
    },
    onError: () => toast.error("Failed to backfill login history"),
  });

  // Compute active/inactive stats from audit logs
  const now = Date.now();
  const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
  const userLastLogin = new Map<string, string>();
  auditLogins.forEach((l) => {
    if (!userLastLogin.has(l.user_id)) userLastLogin.set(l.user_id, l.created_at);
  });
  const activeUsers = Array.from(userLastLogin.entries()).filter(([, ts]) => (now - new Date(ts).getTime()) < thirtyDaysMs);
  const allProfileUserIds = new Set(profiles.map((p) => p.user_id));
  const loggedInUserIds = new Set(userLastLogin.keys());
  const neverLoggedIn = Array.from(allProfileUserIds).filter((id) => !loggedInUserIds.has(id));
  const inactiveUsers = Array.from(userLastLogin.entries()).filter(([, ts]) => (now - new Date(ts).getTime()) >= thirtyDaysMs);

  const recentLogins = auditLogins.slice(0, 10);

  const getProfileName = (userId: string) => {
    const p = profiles.find((pr) => pr.user_id === userId);
    return p?.full_name || null;
  };

  const getProfileCompany = (userId: string) => {
    const p = profiles.find((pr) => pr.user_id === userId);
    return (p?.companies as { name: string } | null)?.name || null;
  };

  // Separate client logins: those who have logged in vs never
  const clientsWithLogins = clientLogins.filter((c) => c.last_sign_in_at || c.total_logins > 0);
  const clientsNeverLoggedIn = clientLogins.filter((c) => !c.last_sign_in_at && c.total_logins === 0);

  // Build a flat list of all client login events for the history tab
  const allClientLoginEvents = clientLogins
    .flatMap((c) => c.logins.map((l) => ({ ...l, full_name: c.full_name, company: c.company })))
    .sort((a, b) => new Date(b.logged_in_at).getTime() - new Date(a.logged_in_at).getTime());

  const stats = [
    { label: "Companies", count: companyCount, icon: Building2, href: "/admin/companies", color: "teal" },
    { label: "Users", count: userCount, icon: Users, href: "/admin/users", color: "lime" },
    { label: "Documents", count: docCount, icon: FileText, href: "/admin/documents", color: "teal" },
    { label: "Pending Questions", count: pendingQuestions, icon: HelpCircle, href: "/admin/questions", color: "lime" },
  ];

  return (
    <AdminLayout>
      <div className="mb-6 sm:mb-10">
        <p className="text-teal/60 text-[11px] font-bold tracking-[4px] uppercase font-body mb-2">Admin Console</p>
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl text-primary-foreground tracking-tight">
          Portal <span className="accent-text">Overview</span>
        </h1>
      </div>

      {/* Vessel Slideshow */}
      <div className="relative w-full aspect-[16/9] rounded-xl sm:rounded-2xl overflow-hidden mb-8 sm:mb-10 shadow-2xl shadow-navy/20 bg-navy-deep">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentSlide}
            src={slideshowImages[currentSlide]}
            alt={`AIRCAT 35 Crewliner - View ${currentSlide + 1}`}
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          />
        </AnimatePresence>
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {slideshowImages.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`w-2 h-2 rounded-full transition-all ${
                i === currentSlide ? "bg-teal w-6" : "bg-primary-foreground/30"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <Link
              key={s.label}
              to={s.href}
              className="glass-card p-4 sm:p-6 rounded-xl sm:rounded-2xl hover:border-teal/20 transition-all group"
            >
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-${s.color}/10 flex items-center justify-center mb-3 sm:mb-4`}>
                <Icon className={`w-4 h-4 sm:w-5 sm:h-5 text-${s.color}`} />
              </div>
              <p className="text-2xl sm:text-3xl font-display text-primary-foreground mb-1">{s.count}</p>
              <p className="text-xs sm:text-sm text-primary-foreground/40 font-body">{s.label}</p>
            </Link>
          );
        })}
      </div>

      {/* Login Activity Summary */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
        <div className="glass-card p-3 sm:p-5 rounded-xl sm:rounded-2xl">
          <div className="flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl bg-teal/10 flex items-center justify-center">
              <UserCheck className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-teal" />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-xl sm:text-2xl font-display text-primary-foreground">{loginsLoading ? "—" : activeUsers.length}</p>
              <p className="text-[10px] sm:text-xs text-primary-foreground/40 font-body">Active (30d)</p>
            </div>
          </div>
        </div>
        <div className="glass-card p-3 sm:p-5 rounded-xl sm:rounded-2xl">
          <div className="flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl bg-amber-500/10 flex items-center justify-center">
              <UserX className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-amber-400" />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-xl sm:text-2xl font-display text-primary-foreground">{loginsLoading ? "—" : inactiveUsers.length}</p>
              <p className="text-[10px] sm:text-xs text-primary-foreground/40 font-body">Inactive (30d+)</p>
            </div>
          </div>
        </div>
        <div className="glass-card p-3 sm:p-5 rounded-xl sm:rounded-2xl">
          <div className="flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl bg-primary-foreground/5 flex items-center justify-center">
              <Clock className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-primary-foreground/30" />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-xl sm:text-2xl font-display text-primary-foreground">{loginsLoading ? "—" : neverLoggedIn.length}</p>
              <p className="text-[10px] sm:text-xs text-primary-foreground/40 font-body">Never Logged In</p>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════ */}
      {/* CLIENT PORTAL ACTIVITY — New dedicated section */}
      {/* ═══════════════════════════════════════════════ */}
      <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Ship className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-teal" />
            <h2 className="font-display text-base sm:text-lg text-primary-foreground">Client Portal Activity</h2>
            <span className="text-[10px] font-bold tracking-[1px] uppercase bg-teal/10 text-teal px-2 py-0.5 rounded-full">
              {clientLogins.length} clients
            </span>
          </div>
          <button
            onClick={() => backfillMutation.mutate()}
            disabled={backfillMutation.isPending}
            className="flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold tracking-[1.5px] uppercase text-teal/60 hover:text-teal font-body transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${backfillMutation.isPending ? "animate-spin" : ""}`} />
            {backfillMutation.isPending ? "Syncing..." : "Sync History"}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4">
          <button
            onClick={() => setClientTab("summary")}
            className={`px-3 py-1.5 text-[11px] font-bold tracking-[1px] uppercase rounded-lg transition-all font-body ${
              clientTab === "summary" ? "bg-teal/15 text-teal" : "text-primary-foreground/30 hover:text-primary-foreground/50"
            }`}
          >
            Summary
          </button>
          <button
            onClick={() => setClientTab("history")}
            className={`px-3 py-1.5 text-[11px] font-bold tracking-[1px] uppercase rounded-lg transition-all font-body ${
              clientTab === "history" ? "bg-teal/15 text-teal" : "text-primary-foreground/30 hover:text-primary-foreground/50"
            }`}
          >
            Login History
          </button>
        </div>

        {clientLoginsLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
          </div>
        ) : clientTab === "summary" ? (
          <div className="space-y-1">
            {/* Clients who have logged in */}
            {clientsWithLogins.length > 0 && (
              <>
                <p className="text-[10px] font-bold tracking-[2px] uppercase text-teal/40 font-body px-2 pt-2 pb-1">
                  Logged In ({clientsWithLogins.length})
                </p>
                {clientsWithLogins.map((client) => {
                  const lastLogin = client.last_sign_in_at || client.last_login_audit;
                  const isExpanded = expandedClient === client.user_id;
                  return (
                    <div key={client.user_id}>
                      <button
                        onClick={() => setExpandedClient(isExpanded ? null : client.user_id)}
                        className="w-full flex items-center justify-between px-2 sm:px-4 py-2.5 sm:py-3 rounded-xl hover:bg-primary-foreground/3 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-teal text-xs font-bold font-body">
                              {(client.full_name || "?")[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm text-primary-foreground/80 font-body truncate">{client.full_name}</p>
                            <p className="text-xs text-primary-foreground/25 font-body truncate">{client.company}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                          <div className="text-right">
                            <p className="text-xs text-primary-foreground/50 font-body">
                              {client.total_logins} login{client.total_logins !== 1 ? "s" : ""}
                            </p>
                            <p className="text-[10px] text-primary-foreground/30 font-body">
                              Last: {lastLogin ? timeAgo(lastLogin) : "Unknown"}
                            </p>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-3.5 h-3.5 text-primary-foreground/20" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5 text-primary-foreground/20" />
                          )}
                        </div>
                      </button>
                      {isExpanded && client.logins.length > 0 && (
                        <div className="ml-14 mr-4 mb-2 space-y-0.5">
                          {client.logins.map((l, idx) => (
                            <div key={idx} className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-primary-foreground/2">
                              <div className="flex items-center gap-2">
                                <LogIn className="w-3 h-3 text-teal/30" />
                                <span className="text-[11px] text-primary-foreground/50 font-body">{formatDateTime(l.logged_in_at)}</span>
                              </div>
                              {l.source === "retroactive_backfill" && (
                                <span className="text-[9px] font-bold tracking-[1px] uppercase text-amber-400/50 font-body">Retroactive</span>
                              )}
                            </div>
                          ))}
                          {client.last_sign_in_at && client.logins.length === 0 && (
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-foreground/2">
                              <LogIn className="w-3 h-3 text-teal/30" />
                              <span className="text-[11px] text-primary-foreground/50 font-body">
                                Auth system: {formatDateTime(client.last_sign_in_at)}
                              </span>
                              <span className="text-[9px] font-bold tracking-[1px] uppercase text-amber-400/50 font-body">Auth Record</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {/* Clients who have never logged in */}
            {clientsNeverLoggedIn.length > 0 && (
              <>
                <p className="text-[10px] font-bold tracking-[2px] uppercase text-primary-foreground/20 font-body px-2 pt-4 pb-1">
                  Never Logged In ({clientsNeverLoggedIn.length})
                </p>
                {clientsNeverLoggedIn.map((client) => (
                  <div key={client.user_id} className="flex items-center justify-between px-2 sm:px-4 py-2.5 sm:py-3 rounded-xl hover:bg-primary-foreground/3 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-primary-foreground/5 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary-foreground/20 text-xs font-bold font-body">
                          {(client.full_name || "?")[0].toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-primary-foreground/40 font-body truncate">{client.full_name}</p>
                        <p className="text-xs text-primary-foreground/15 font-body truncate">{client.company}</p>
                      </div>
                    </div>
                    <span className="text-[10px] text-primary-foreground/15 font-body flex-shrink-0 ml-4">No activity</span>
                  </div>
                ))}
              </>
            )}

            {clientLogins.length === 0 && (
              <p className="text-sm text-primary-foreground/30 font-body py-6 text-center">No client accounts found.</p>
            )}
          </div>
        ) : (
          /* Login History Tab - chronological list */
          <div className="space-y-1">
            {allClientLoginEvents.length === 0 ? (
              <p className="text-sm text-primary-foreground/30 font-body py-6 text-center">
                No client login events recorded yet. Click "Sync History" to pull retroactive data.
              </p>
            ) : (
              allClientLoginEvents.map((event, idx) => (
                <div key={idx} className="flex items-center justify-between px-2 sm:px-4 py-2.5 sm:py-3 rounded-xl hover:bg-primary-foreground/3 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-teal text-xs font-bold font-body">
                        {(event.full_name || event.email || "?")[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-primary-foreground/80 font-body truncate">{event.full_name || event.email}</p>
                      <p className="text-xs text-primary-foreground/25 font-body truncate">{event.company} · {event.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <div className="text-right">
                      <p className="text-xs text-primary-foreground/50 font-body whitespace-nowrap">
                        {formatDateTime(event.logged_in_at)}
                      </p>
                    </div>
                    {event.source === "retroactive_backfill" && (
                      <span className="text-[9px] font-bold tracking-[1px] uppercase text-amber-400/50 font-body">Retro</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Recent Login Activity (All users) */}
      <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <LogIn className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-teal" />
            <h2 className="font-display text-base sm:text-lg text-primary-foreground">Recent Login Activity</h2>
          </div>
          <Link
            to="/admin/users"
            className="text-[10px] sm:text-[11px] font-bold tracking-[1.5px] uppercase text-teal/60 hover:text-teal font-body transition-colors"
          >
            View All →
          </Link>
        </div>
        {loginsLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
          </div>
        ) : recentLogins.length === 0 ? (
          <p className="text-sm text-primary-foreground/30 font-body py-6 text-center">No login activity recorded yet.</p>
        ) : (
          <div className="space-y-1">
            {recentLogins.map((u, idx) => {
              const name = getProfileName(u.user_id);
              const company = getProfileCompany(u.user_id);
              return (
                <div key={`${u.user_id}-${idx}`} className="flex items-center justify-between px-2 sm:px-4 py-2.5 sm:py-3 rounded-xl hover:bg-primary-foreground/3 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-teal/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-teal text-xs font-bold font-body">
                        {(name || u.email || "?")[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-primary-foreground/80 font-body truncate">
                        {name || u.email}
                      </p>
                      <p className="text-xs text-primary-foreground/25 font-body truncate">
                        {company ? `${company} · ` : ""}{u.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 ml-4">
                    <LogIn className="w-3 h-3 text-teal/40" />
                    <span className="text-xs text-primary-foreground/40 font-body whitespace-nowrap">
                      {timeAgo(u.created_at)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-6">
        <h2 className="font-display text-lg sm:text-xl text-primary-foreground mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
          {[
            { label: "Add Company", href: "/admin/companies", desc: "Onboard a new client company" },
            { label: "Upload Document", href: "/admin/documents", desc: "Share files with a client" },
            { label: "Answer Questions", href: "/admin/questions", desc: "Respond to pending inquiries" },
            { label: "Post Update", href: "/admin/progress", desc: "Update project progress" },
            { label: "Manage Users", href: "/admin/users", desc: "View and manage client users" },
          ].map((a) => (
            <Link
              key={a.label}
              to={a.href}
              className="p-4 rounded-xl bg-primary-foreground/3 hover:bg-primary-foreground/6 border border-primary-foreground/5 hover:border-teal/20 transition-all"
            >
              <p className="text-sm font-body font-medium text-primary-foreground/80">{a.label}</p>
              <p className="text-xs text-primary-foreground/30 font-body mt-0.5">{a.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
